import React, { useContext, useEffect, useState } from "react";
import { useMutation } from "react-query";
import GlobalContext from "src/context/global-context";
import { useIsMounted } from "src/lib/hooks/use-is-mounted";
import client from "../../api";
import { EditIcon } from "src/components/icons/edit-icon";
import { GreenTickIcon } from "src/components/icons/green-tick-icon";
import {
  APISomethingWrongMsg,
  emailTemplateFilterOptions,
  NetworkDisconnectMsg,
  permissions as permissionConstants,
} from "src/lib/constants";
import usePermissions from "src/hooks/use-permissions";
import PermissionGate from "src/components/shared/permission-gate";
import { getErrorMessage } from "src/lib/utils";
import { Table, Button, Modal, Input, Select, Form, Checkbox } from "antd";
import ToggleSwitch from "src/components/ui/switch";
import { ColumnsType } from "antd/es/table";
import SearchFilter from "src/components/search-filters/reset2fa-filter";

import {
  EditorProvider,
  Editor,
  Toolbar,
  BtnBold,
  BtnItalic,
  BtnUnderline,
  BtnStrikeThrough,
  BtnNumberedList,
  BtnBulletList,
  BtnLink,
  BtnUndo,
  BtnRedo,
  createButton,
} from "react-simple-wysiwyg";
import { ILanguagesList } from "src/api/types";
import CurrencyDrop from "src/components/ui/dropdown-list";

const { TextArea } = Input;
const { Option } = Select;

// Utility function to extract the content inside the <body> tag
const getBodyContent = (fullHtml: string): string => {
  const bodyMatch = fullHtml.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
  if (bodyMatch && bodyMatch[1]) {
    return bodyMatch[1].trim();
  }
  return fullHtml.trim();
};

// Utility function to extract the outer structure
const getOuterHtml = (fullHtml: string) => {
  // 1. Extract content before <body>
  const beforeBodyMatch = fullHtml.match(/^([\s\S]*?)<body[^>]*>/i);
  let beforeBodyContent = beforeBodyMatch
    ? beforeBodyMatch[1] + beforeBodyMatch[0].match(/<body[^>]*>/i)![0]
    : "<html><head></head><body>";

  // 2. Ensure DOCTYPE is at the very beginning
  if (!beforeBodyContent.trim().startsWith("<!DOCTYPE")) {
    beforeBodyContent = "<!DOCTYPE html>\n" + beforeBodyContent;
  } else {
    // Clean up extra spaces/newlines before DOCTYPE
    beforeBodyContent = beforeBodyContent.trim();
  }

  // 3. Extract content after </body>
  const afterBodyMatch = fullHtml.match(/<\/body>([\s\S]*?)$/i);
  let afterBodyContent = afterBodyMatch ? afterBodyMatch[1] : "</body></html>";

  // Ensure the body closing tag is present if it was stripped
  if (!afterBodyContent.includes("</body>")) {
    afterBodyContent = "</body>" + afterBodyContent;
  }

  // Ensure closing </html> if missing
  if (!afterBodyContent.includes("</html>")) {
    afterBodyContent = afterBodyContent + "</html>";
  }

  return { beforeBody: beforeBodyContent, afterBody: afterBodyContent };
};

// ...

// Custom toolbar buttons
const BtnAlignLeft = createButton("Align Left", "🡸", "justifyLeft");
const BtnAlignCenter = createButton("Align Center", "☰", "justifyCenter");
const BtnAlignRight = createButton("Align Right", "🡺", "justifyRight");
const BtnToggleHtml = ({ onToggle, isHtmlMode }: any) => (
  <button
    type="button"
    title="Toggle HTML view"
    className={`rsw-btn ${isHtmlMode ? "!bg-blue-100 !text-blue-600" : ""}`}
    onClick={onToggle}
  >
    {"</>"}
  </button>
);

const EmailTemplate = () => {
  const { canView, canList, canAdd, canEdit } = usePermissions(
    permissionConstants.EMAIL_TEMP
  );
  const canViewTemplates = canView || canList;

  const [dataSource, setDataSource] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [totalItems, setTotalItems] = useState<number>(0);

  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
  const [editorContent, setEditorContent] = useState("");
  const [isHtmlMode, setIsHtmlMode] = useState(false);

  const [editingRowId, setEditingRowId] = useState<number | null>(null);
  const [editingSubject, setEditingSubject] = useState<string>("");

  const [languages, setLanguages] = useState<ILanguagesList[] | undefined>();
  const [languageList, setLanguageList] = useState<string[]>();
  const [selectedLanguage, setSelectedLanguage] = useState({
    name: "English",
    code: "EN",
  });

  const [filter, setFilter] = useState("type");
  const [searchText, setSearchText] = useState("");

  // NEW: Add Template Modal
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [addForm] = Form.useForm();
  const [addEditorContent, setAddEditorContent] = useState("");
  const [addIsHtmlMode, setAddIsHtmlMode] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [outerHtmlStructure, setOuterHtmlStructure] = useState<{
    beforeBody: string;
    afterBody: string;
  } | null>(null);

  const { setIsAlertOpened, setAlertText } = useContext(GlobalContext);
  const isMounted = useIsMounted();

  const isWideHtml = (html: string) =>
    /<table|<pre|style\s*=\s*"(?:[^"]*width\s*:\s*\d)/i.test(html || "");
  const editorIsWide = isWideHtml(editorContent);

  const { mutate: getLanguages, isLoading: languageLoading } = useMutation(
    client.translations.allLanguages,
    {
      onSuccess(data: any) {
        setLanguages(data.data);
        let list = data.data.map((item: any) => item.name);
        setLanguageList(list);
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else {
          if (error.response) {
            setAlertText(getErrorMessage(error.response.data));
          } else {
            setAlertText(APISomethingWrongMsg);
          }
        }
        setIsAlertOpened(true);
      },
    }
  );

  const handleLanguage = (e: any) => {
    let langObj = languages?.find((item: any) => item.name === e);
    setSelectedLanguage({
      name: langObj?.name ?? "",
      code: langObj?.code ?? "",
    });
  };
  /** ---------------- Fetch Templates API ---------------- */
  const { mutate: getTemplatesList, isLoading } = useMutation(
    (params: {
      page: number;
      limit: number;
      search_field?: string;
      search_value?: string;
    }) => client.emailTemplate.getAllTemplates(params),

    {
      onSuccess: (data) => {
        setDataSource(
          data.items.map((item: any) => ({
            ...item,
          }))
        );
        setTotalItems(data.totalcount || 0);
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") setAlertText(NetworkDisconnectMsg);
        else if (error.response)
          setAlertText(getErrorMessage(error.response.data));
        else setAlertText(APISomethingWrongMsg);
        setIsAlertOpened(true);
      },
    }
  );

  /** ---------------- Update Template API ---------------- */
  const { mutate: updateTemplate, isLoading: isUpdating } = useMutation(
    client.emailTemplate.updateTemplate,
    {
      onSuccess: (data) => {
        setIsEditorOpen(false);
        setAlertText(data.message);
        setIsAlertOpened(true);
        getTemplatesList({ page: currentPage, limit: pageSize });
      },
      onError: (error: any) => {
        setAlertText(
          error.response
            ? getErrorMessage(error.response.data)
            : APISomethingWrongMsg
        );
        setIsAlertOpened(true);
      },
    }
  );

  /** ---------------- Add Template API ---------------- */
  const { mutate: addTemplate, isLoading: isAdding } = useMutation(
    client.emailTemplate.addTemplate,
    {
      onSuccess: (data) => {
        setIsAddModalOpen(false);
        addForm.resetFields();
        setAddEditorContent("");
        setAlertText(data.message);
        setIsAlertOpened(true);
        getTemplatesList({ page: currentPage, limit: pageSize });
      },
      onError: (error: any) => {
        setAlertText(
          error.response
            ? getErrorMessage(error.response.data)
            : APISomethingWrongMsg
        );
        setIsAlertOpened(true);
      },
    }
  );

  /** ---------------- Fetch Initial Data ---------------- */
  useEffect(() => {
    if (!isMounted || !canViewTemplates) return;

    getTemplatesList({
      page: currentPage,
      limit: pageSize,
      search_field: filter || undefined,
      search_value: searchText || undefined,
    });
    getLanguages();
  }, [isMounted, canViewTemplates, currentPage, pageSize, filter, searchText]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filter, searchText]);

  /** ---------------- Table Columns ---------------- */
  const columns: ColumnsType<any> = [
    {
      title: "Template type",
      dataIndex: "template_type",
      key: "template_type",
      render: (value) => <span className="text-primary">{value}</span>,
    },

    { title: "Notification type", dataIndex: "email_type", key: "email_type" },

    { title: "Type", dataIndex: "type", key: "type" },
    { title: "Language", dataIndex: "language", key: "language" },

    {
      title: "Subject",
      dataIndex: "subject",
      key: "subject",
      render: (subject, record) => {
        const isEditing = editingRowId === record.id;

        return (
          <div className="flex items-center gap-2">
            {isEditing && canEdit ? (
              <>
                <TextArea
                  value={editingSubject}
                  onChange={(e) => setEditingSubject(e.target.value)}
                  className="!w-[200px]"
                />

                <div className="w-5 h-5 flex items-center justify-center shrink-0">
                  <GreenTickIcon
                    className="cursor-pointer text-green-600 w-4 h-4"
                    onClick={() => {
                      if (!canEdit) return;
                      const payload = { ...record, subject: editingSubject };
                      updateTemplate(payload);
                      setEditingRowId(null);
                    }}
                  />
                </div>
              </>
            ) : (
              <>
                <span>{subject}</span>

                {canEdit && (
                  <div className="w-5 h-5 flex items-center justify-center shrink-0">
                    <EditIcon
                      className="cursor-pointer text-gray-500 hover:text-primary w-4 h-4"
                      onClick={() => {
                        setEditingRowId(record.id);
                        setEditingSubject(subject);
                      }}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        );
      },
    },

    { title: "Description", dataIndex: "description", key: "description" },
    { title: "Updated by", dataIndex: "updated_by", key: "updated_by" },

    // { title: "Body text", dataIndex: "body_text", key: "body_text" },

    {
      title: "Status",
      dataIndex: "is_active",
      key: "is_active",
      render: (is_active, record) => {
        const isDisabled = !canEdit || isUpdating;
        return (
          <div
            className={`flex flex-row gap-2 items-center ${isDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
            title={!canEdit ? "Read only - No edit permission" : undefined}
          >
            <ToggleSwitch
              type={`${record.id}`}
              value={!!is_active}
              disabled={isDisabled}
              onValueChanged={(_: any, newValue: boolean) => {
                if (!canEdit) {
                  setAlertText("You do not have permission to update email templates");
                  setIsAlertOpened(true);
                  return;
                }
                const payload = {
                  ...record,
                  is_active: newValue,
                  updated_by: record.updated_by,
                };
                updateTemplate(payload);
              }}
            />
          </div>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => {
        if (!canView && !canEdit) {
          return "—";
        }
        return (
          <div className="flex gap-2">
            {canView && (
              <Button
                type="default"
                size="small"
                className="!text-gray-700 !border-gray-300 hover:!border-primary hover:!text-primary"
                onClick={() => {
                  const isSms = record.type === "sms";
                  const content = isSms ? record.body_text : record.body_html;
                  const wide = !isSms && isWideHtml(content);

                  Modal.info({
                    title: `Template Preview (${isSms ? "SMS" : "Email"})`,
                    width: wide ? "95vw" : 800,
                    bodyStyle: {
                      maxHeight: "75vh",
                      overflowY: "auto",
                      overflowX: "hidden", // prevent modal from widening
                      padding: "0",
                    },

                    okText: "Close",
                    okButtonProps: {
                      className: "!bg-primary !text-white hover:!bg-primary-dark",
                    },
                    content: isSms ? (
                      <pre className="whitespace-pre-wrap font-manrope text-sm">
                        {content || "No content available"}
                      </pre>
                    ) : (
                      <div
                        style={{
                          maxWidth: "100%",
                          overflowX: "auto", // horizontal scroll INSIDE the modal
                          padding: 15,
                        }}
                      >
                        <div
                          className="email-preview-container"
                          dangerouslySetInnerHTML={{ __html: content || "" }}
                        />
                      </div>
                    ),
                  });
                }}
              >
                View
              </Button>
            )}

          {canEdit && (
            <Button
              type="primary"
              size="small"
              className="!bg-primary !text-white hover:!bg-primary-dark"
              onClick={() => {
                setSelectedTemplate(record);

                const isSms = record?.type === "sms";
                const fullContent = isSms
                  ? record.body_text
                  : record.body_html || "";

                // Extract inner content for the editor
                const innerContent = isSms
                  ? fullContent // SMS templates usually don't have HTML/Body tags
                  : getBodyContent(fullContent);

                setEditorContent(innerContent);

                // Only process and store outer structure for HTML emails
                if (!isSms) {
                  // NOTE: Passing only fullContent as getOuterHtml no longer needs innerContent
                  const { beforeBody, afterBody } = getOuterHtml(fullContent);
                  setOuterHtmlStructure({ beforeBody, afterBody });
                } else {
                  setOuterHtmlStructure(null);
                }

                setIsEditorOpen(true);
              }}
            >
              Edit
            </Button>
          )}
        </div>
        );
      },
    },
  ];

  /** ---------------- Save Template Update ---------------- */
  const handleSave = () => {
    if (!selectedTemplate) return;

    if (!canEdit) {
      setAlertText("You do not have permission to update email templates");
      setIsAlertOpened(true);
      return;
    }

    let finalContent = editorContent;

    // RECONSTRUCTION LOGIC: Wrap the new content with the preserved outer structure
    if (selectedTemplate.type === "email" && outerHtmlStructure) {
      // outerHtmlStructure.beforeBody now includes <!DOCTYPE html>
      finalContent = `${outerHtmlStructure.beforeBody}\n${editorContent}\n${outerHtmlStructure.afterBody}`;
    }

    const payload = {
      ...selectedTemplate,
      ...(selectedTemplate.type === "sms"
        ? { body_text: finalContent }
        : { body_html: finalContent }),
    };

    setOuterHtmlStructure(null);

    updateTemplate(payload);
  };

  /** ---------------- Handle Add Template ---------------- */
  const handleAddSubmit = (values: any) => {
    if (!canAdd) {
      setAlertText("You do not have permission to add email templates");
      setIsAlertOpened(true);
      return;
    }

    if (values.type === "email" && !addEditorContent.trim()) {
      setAlertText("HTML body is required");
      setIsAlertOpened(true);
      return;
    }

    const payload = {
      ...values,
      language: selectedLanguage.code.toLowerCase(),
      body_html: values.type === "email" ? addEditorContent : null,
      body_text: values.type === "sms" ? values.body_text : null,
      is_active: isActive,
    };

    addTemplate(payload);
  };

  const selectedType = Form.useWatch("type", addForm);

  return (
    <div className="w-full h-full bg-gray">
      <div className="flex justify-between items-center p-2 sm:p-4">
        <p className="text-[25px] sm:text-[32px] font-manrope text-primary font-medium">
          Email Templates
        </p>
        {canViewTemplates && (
          <SearchFilter
            options={emailTemplateFilterOptions}
            placeholder="Search filters"
            value={filter}
            onChange={setFilter}
            searchText={searchText}
            setSearchText={setSearchText}
          />
        )}
        <PermissionGate
          module={[permissionConstants.EMAIL_TEMP]}
          action="can_add"
        >
          <Button
            type="primary"
            className="!bg-primary !text-white hover:!bg-primary-dark"
            onClick={() => setIsAddModalOpen(true)}
          >
            + Add Template
          </Button>
        </PermissionGate>
      </div>

      {!canViewTemplates ? (
        <div className="flex flex-col items-center justify-center bg-white rounded-[12px] m-2 sm:m-4 p-8 text-center shadow-sm">
          <p className="font-manrope text-[16px] font-semibold text-secondary">
            You do not have permission to view email templates.
          </p>
        </div>
      ) : (
        <div className="flex flex-col w-full bg-white rounded-[12px] p-4 mt-2 sm:mt-4">
          <Table
            loading={isLoading}
            showSorterTooltip={false}
            columns={columns}
            dataSource={dataSource}
            pagination={{
              total: totalItems,
              current: currentPage,
              onChange: (page, size) => setCurrentPage(page),
              position: ["bottomCenter"],
              showSizeChanger: false,
            }}
          />
        </div>
      )}

      {/* ---------------- Edit Template Modal ---------------- */}
      <Modal
        title={`Edit Template - ${selectedTemplate?.subject || ""}`}
        open={isEditorOpen}
        onCancel={() => {
          setIsEditorOpen(false);
          setSelectedTemplate(null);
          setEditorContent("");
        }}
        onOk={handleSave}
        confirmLoading={isUpdating}
        width={editorIsWide ? "90vw" : 1000}
        okText="Update"
        centered
        bodyStyle={{ maxHeight: "75vh", overflow: "auto" }}
        okButtonProps={{
          className: "!bg-primary !text-white hover:!bg-primary-dark",
        }}
        cancelButtonProps={{
          className: "!text-gray-700 hover:!text-primary",
        }}
      >
        <div className="border rounded-md p-2 bg-white min-h-[400px]">
          <EditorProvider>
            <div className="rounded-md border overflow-hidden bg-white">
              <Toolbar className="flex flex-wrap gap-2 p-2 bg-gray-100 border-b">
                {/* Basic Formatting */}
                <BtnBold />
                <BtnItalic />
                <BtnUnderline />
                <BtnStrikeThrough />

                <div className="border-l h-5 mx-2" />

                {/* Lists */}
                <BtnNumberedList />
                <BtnBulletList />

                <div className="border-l h-5 mx-2" />

                {/* Alignment */}
                <BtnAlignLeft />
                <BtnAlignCenter />
                <BtnAlignRight />

                <div className="border-l h-5 mx-2" />

                {/* Links */}
                <BtnLink />

                <div className="border-l h-5 mx-2" />

                {/* Divider */}
                <button
                  type="button"
                  className="rsw-btn"
                  title="Insert Divider"
                  onClick={() =>
                    document.execCommand("insertHTML", false, "<hr/>")
                  }
                >
                  ―
                </button>

                <div className="border-l h-5 mx-2" />

                {/* Undo / Redo */}
                <BtnUndo />
                <BtnRedo />

                <div className="border-l h-5 mx-2" />

                {/* HTML View Toggle */}
                <BtnToggleHtml
                  onToggle={() => setIsHtmlMode(!isHtmlMode)}
                  isHtmlMode={isHtmlMode}
                />
              </Toolbar>

              {/* Editor content */}
              {isHtmlMode ? (
                <textarea
                  value={editorContent}
                  onChange={(e) => setEditorContent(e.target.value)}
                  className="w-full h-[400px] p-2 font-mono text-sm border-t outline-none"
                />
              ) : (
                <Editor
                  value={editorContent}
                  onChange={(e: any) => setEditorContent(e.target.value)}
                  className="min-h-[400px]"
                />
              )}
            </div>
          </EditorProvider>
        </div>
      </Modal>

      {/* ---------------- Add Template Modal ---------------- */}
      <Modal
        title="Add New Template"
        open={isAddModalOpen}
        onCancel={() => setIsAddModalOpen(false)}
        footer={null}
        width={1000}
        centered
        getContainer={() => document.body}
        zIndex={10000} // ✅ make sure it's above header (9999)
      >
        {/* 🔹 Add Template Form */}
        <Form
          layout="vertical"
          form={addForm}
          onFinish={handleAddSubmit}
          initialValues={{ type: "email" }}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Form.Item
              label="Language"
              name="language"
              rules={[{ required: true, message: "Please select a language" }]}
            >
              <CurrencyDrop
                value={selectedLanguage.name}
                options={languageList || []}
                onChange={handleLanguage}
                type="language"
              />
            </Form.Item>
            <Form.Item
              label="Subject"
              name="subject"
              rules={[{ required: true, message: "Please enter subject" }]}
            >
              <Input placeholder="Enter subject" />
            </Form.Item>

            <Form.Item
              label="Template Type"
              name="template_type"
              rules={[
                { required: true, message: "Please enter template type" },
              ]}
            >
              <Input placeholder="Enter template type" />
            </Form.Item>

            <Form.Item
              label="Notification Type"
              name="email_type"
              rules={[{ required: true, message: "Please select type" }]}
            >
              <Select getPopupContainer={(trigger) => trigger.parentNode}>
                <Option value="admin">Admin</Option>
                <Option value="user">User</Option>
              </Select>
            </Form.Item>

            <Form.Item
              label="Type"
              name="type"
              rules={[{ required: true, message: "Please select type" }]}
            >
              <Select getPopupContainer={(trigger) => trigger.parentNode}>
                <Option value="email">Email</Option>
                <Option value="sms">SMS</Option>
              </Select>
            </Form.Item>

            <Form.Item
              label="Description"
              name="description"
              rules={[{ required: true, message: "Please enter description" }]}
            >
              <TextArea rows={2} placeholder="Enter description" />
            </Form.Item>
            {/* ------- Body TEXT (SMS Only) ------- */}
            {selectedType === "sms" && (
              <Form.Item
                label="Body Text"
                name="body_text"
                rules={[{ required: true, message: "Please enter body text" }]}
              >
                <TextArea rows={3} placeholder="Enter body text" />
              </Form.Item>
            )}
          </div>

          <div className="flex items-center mb-4">
            <Checkbox
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            >
              Active
            </Checkbox>
          </div>

          {/* ------- Body HTML (EMAIL Only) ------- */}
          {selectedType === "email" && (
            <>
              <Form.Item
                label="Body (HTML)"
                required
                validateStatus={addEditorContent.trim() === "" ? "error" : ""}
                help={
                  addEditorContent.trim() === "" ? "HTML body is required" : ""
                }
              >
                <EditorProvider>
                  <div className="rounded-md border overflow-hidden bg-white">
                    <Toolbar className="flex flex-wrap gap-2 p-2 bg-gray-100 border-b">
                      <BtnBold />
                      <BtnItalic />
                      <BtnUnderline />
                      <BtnStrikeThrough />
                      <div className="border-l h-5 mx-2" />
                      <BtnNumberedList />
                      <BtnBulletList />
                      <div className="border-l h-5 mx-2" />
                      <BtnAlignLeft />
                      <BtnAlignCenter />
                      <BtnAlignRight />
                      <div className="border-l h-5 mx-2" />
                      <BtnLink />
                      <BtnUndo />
                      <BtnRedo />
                      <BtnToggleHtml
                        onToggle={() => setAddIsHtmlMode(!addIsHtmlMode)}
                        isHtmlMode={addIsHtmlMode}
                      />
                    </Toolbar>

                    {addIsHtmlMode ? (
                      <textarea
                        value={addEditorContent}
                        onChange={(e) => setAddEditorContent(e.target.value)}
                        className="w-full h-[300px] p-2 font-mono text-sm border-t outline-none"
                      />
                    ) : (
                      <Editor
                        value={addEditorContent}
                        onChange={(e: any) =>
                          setAddEditorContent(e.target.value)
                        }
                        className="min-h-[300px]"
                      />
                    )}
                  </div>
                </EditorProvider>
              </Form.Item>
            </>
          )}

          <div className="flex justify-end mt-6">
            <Button
              type="default"
              className="mr-2 !text-gray-700 hover:!text-primary"
              onClick={() => setIsAddModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={isAdding}
              className="!bg-primary !text-white hover:!bg-primary-dark"
            >
              Submit
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default EmailTemplate;
